import {
    Dialog,
    DialogContent,
    DialogTitle
} from 'components/ui/dialog';
import { Button } from "../../../components/ui/button"
import { InfoCircle, Refresh, Xmark } from 'iconoir-react';
import { NewProjectModalProps, Project } from "../types/create-project";
import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { useCreateProject } from '../hooks/use-create-project';
import { CreateProjectRequest } from '../services/projects-services';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { useQueryClient } from '@tanstack/react-query';
import { addProjectToSidebarAndCache } from '../helpers/sync-project-sidebar';
import { DEFAULT_PROJECT_COLOR, ProjectColorSwatches } from './project-color-swatches';

export const NewProjectModal = ({ isOpen, onClose, newConversatioId, callback }: NewProjectModalProps) => {
    const [name, setName] = useState("");
    const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_PROJECT_COLOR);
    const maxLength = 50;

    const { mutateAsync: createProject, isPending } = useCreateProject();
    const { t } = useTranslation();
    const { toast } = useToast();
    const { theme } = useTheme();
    const queryClient = useQueryClient();
    const textColor = theme === 'dark' ? 'text-true-white' : 'text-grey-700';
    const closeIconColor = theme === 'dark' ? 'text-true-white' : 'text-grey-500';

    const handleCreateProject = async () => {
        if (!name) return;
        const payload: CreateProjectRequest = {
            name: name,
            conversationId: newConversatioId,
            color: selectedColor
        }
        try {
            const response = await createProject({ payload });
            const newProject: Project = {
                id: response.id,
                name: response.name,
                items: [],
                prompts: response.prompts,
                color: response.color ?? selectedColor
            }
            addProjectToSidebarAndCache(queryClient, newProject);
            callback?.();
        } catch (error) {
            const detail =
                (error as { error?: { detail?: string } })?.error?.detail;
            toast({
                variant: "destructive",
                title: t('ERROR') || 'Error',
                description:
                    detail ||
                    t('FAILED_TO_CREATE_PROJECT') ||
                    'Failed to create project.',
            });
        }
        onClose();
        setName("");
        setSelectedColor(DEFAULT_PROJECT_COLOR);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setName(e.target.value);
    };

    useEffect(() => {
        if (isOpen) {
            setName("");
            setSelectedColor(DEFAULT_PROJECT_COLOR);
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                hideClose
                overlayClassName="z-[145]"
                className="z-[150] flex max-h-[90vh] w-full max-w-[565px] flex-col gap-6 overflow-y-auto rounded-xl border border-grey-100 bg-card p-4 shadow-lg dark:border-black-500 sm:p-8"
            >
                <button
                    type="button"
                    aria-label={t('CLOSE') || 'Close'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className={cn(
                        'absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center focus:outline-none sm:h-6 sm:w-6',
                        closeIconColor
                    )}
                >
                    <Xmark width={14} height={14} />
                </button>

                <DialogTitle className={cn('self-stretch break-words pr-10 font-sans text-2xl font-semibold not-italic leading-heading tracking-normal', textColor)}>
                    {t('NEW_PROJECT') || 'New Project'}
                </DialogTitle>

                <div className="flex w-full items-start gap-2 rounded bg-warning-background p-4">
                    <div className="flex shrink-0 items-start px-px pb-1 pt-px">
                        <InfoCircle
                            width={13.333}
                            height={13.333}
                            strokeWidth={1.25}
                            className="shrink-0 text-warning-high-emphasis"
                        />
                    </div>
                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="self-stretch break-words font-sans text-xs font-semibold not-italic leading-heading text-warning-high-emphasis">
                            {t('WHAT_ARE_PROJECTS') || 'What are projects?'}
                        </p>
                        <p className="self-stretch break-words font-sans text-xs font-normal not-italic leading-heading text-warning-high-emphasis">
                            {t('WHAT_ARE_PROJECTS_DESCRIPTION') ||
                                'Projects let you organize chats and add instructions & files to groups of chats.'}
                        </p>
                    </div>
                </div>

                <div className="flex w-full flex-col items-start gap-8 self-stretch pb-4">
                    <div className="flex w-full flex-col items-start gap-1 self-stretch">
                        <label className={cn('font-sans text-sm font-medium not-italic leading-body', textColor)}>
                            {t('NAME') || 'Name'}
                        </label>
                        <Input
                            onClick={(e) => e.stopPropagation()}
                            type="text"
                            placeholder={t('PROJECT_NAME_PLACEHOLDER') || 'e.g. Q1 Marketing Planning'}
                            value={name}
                            onChange={handleChange}
                            maxLength={maxLength}
                            onKeyDown={(e) => e.key === "Enter" && !isPending && name.trim() && handleCreateProject()}
                            className={cn(
                                'flex !h-12 w-full items-center gap-4 self-stretch rounded-lg border border-grey-100 bg-background px-5 py-4 font-sans text-base font-normal not-italic leading-heading shadow-none placeholder:font-normal placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring md:text-sm dark:border-black-500 dark:bg-transparent dark:placeholder:text-grey-400',
                                textColor
                            )}
                        />
                    </div>

                    <div className="flex w-full flex-col items-start self-stretch">
                        <div className="flex w-full flex-col items-start gap-4 self-stretch">
                            <label className={cn('font-sans text-sm font-bold not-italic leading-heading', textColor)}>
                                {t('COLOR') || 'Color'}

                            </label>
                            <ProjectColorSwatches
                                selectedColor={selectedColor}
                                onSelect={setSelectedColor}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col-reverse gap-3 self-stretch border-t border-grey-100 pt-6 dark:border-border sm:flex-row sm:items-center sm:justify-end sm:gap-4">
                    <Button
                        variant="outline"
                        className={cn(
                            'h-11 w-full gap-1.5 rounded-full border px-4 text-base font-medium leading-heading shadow-none sm:w-auto sm:min-w-[101px]',
                            theme === 'dark'
                                ? 'border-black-500 bg-transparent text-true-white hover:bg-sidebar-pill-dark hover:text-true-white'
                                : 'border-grey-200 bg-card text-grey-950 hover:bg-grey-50 hover:text-grey-950'
                        )}
                        onClick={(e) => { e.stopPropagation(); onClose() }}
                    >
                        {t('CANCEL') || 'Cancel'}
                    </Button>
                    <Button
                        className={cn(
                            'h-11 w-full gap-1.5 rounded-full border-0 px-4 text-base font-semibold leading-heading text-true-white shadow-none sm:w-[175px]',
                            '!bg-black-base hover:!bg-grey-900',
                            'disabled:!bg-grey-200 disabled:!text-grey-500 disabled:!opacity-100',
                            'dark:!bg-grey-900 dark:hover:!bg-grey-700 dark:text-true-white',
                            'disabled:dark:!bg-black-400 disabled:dark:!text-grey-500 disabled:dark:!opacity-100'
                        )}
                        onClick={(e) => { e.stopPropagation(); handleCreateProject() }}
                        disabled={isPending || !name.trim()}
                    >
                        {t('CREATE_PROJECT') || 'Create Project'}
                        {isPending && (
                            <Refresh className="h-4 w-4 animate-spin" />
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
